import { ActionTree } from 'vuex';
import {
  ISettingsBillingState,
  IBillingDetails,
  IUpdateDetails,
  EnumChargeBeeState,
} from '../stores/settings-billing.store';
import { RootState } from '../store';
import {
  mutationError,
  mutationLoading,
  mutationSetBillingDetails,
  mutationSetBillingCurrency,
  mutationSetPlan,
  mutationSetPlanType,
  mutationSetShowContactSalesModal,
  mutationSetUserCount,
  mutationSaving,
  mutationSetShowUpdatePlanModal,
  mutationSetShowConfirmStarterModal,
  mutationSetUpdateDetails,
  mutationSetChargebeeState,
} from '../mutations/settings-billing.mutations';
import {
  getBillingDetails,
  setBillingCurrency,
  upgrade,
  getUpgradeEstimate,
} from '@/services/settings.service';
import { IPlan } from '@/types/Plans';
import { EnumPlans } from '@/enums/Plans';

export const actionLoadBillingDetails = 'loadBillingDetails';
export const actionSetUserCount = 'setUserCount';
export const actionSetBillingCurrency = 'setBillingCurrency';
export const actionSetPlan = 'setPlan';
export const actionSetPlanType = 'setPlanType';
export const actionShowConfirmStarterModal = 'setShowConfirmStarterModal';
export const actionShowContactSalesModal = 'setShowContactSalesModal';
export const actionShowUpdatePlanModal = 'setShowUpdatePlanModal';
export const actionSetUpdateDetails = 'setUpdateDetails';
export const actionSetChargebeeState = 'setChargebeeState';
export const actionUpgrade = 'upgrade';

export const actions: ActionTree<ISettingsBillingState, RootState> = {
  async [actionSetUserCount]({ commit }, count: number) {
    commit(mutationSetUserCount, count);
  },
  async [actionLoadBillingDetails]({ commit }) {
    commit(mutationLoading, true);
    try {
      const billingDetails: IBillingDetails = await getBillingDetails();
      commit(mutationSetBillingDetails, billingDetails);
      commit(mutationError, '');
      commit(mutationLoading, false);
    } catch (e) {
      commit(mutationError, e.toString());
      commit(mutationLoading, false);
    }
  },
  async [actionSetPlan]({ commit }, plan: IPlan) {
    commit(mutationSetPlan, plan);
  },
  async [actionSetPlanType]({ commit }, planType: EnumPlans) {
    commit(mutationSetPlanType, planType);
  },
  async [actionShowContactSalesModal]({ commit }, payload: boolean) {
    commit(mutationSetShowContactSalesModal, payload);
  },
  async [actionShowUpdatePlanModal]({ commit }, payload: boolean) {
    commit(mutationSetShowUpdatePlanModal, payload);
  },
  async [actionShowConfirmStarterModal]({ commit }, payload: boolean) {
    commit(mutationSetShowConfirmStarterModal, payload);
  },
  async [actionSetBillingCurrency]({ commit, state }, { entityId, currency }) {
    const originalCurrency = state.billingCurrency;
    commit(mutationSetBillingCurrency, currency);
    const status = await setBillingCurrency(entityId, currency);
    if (status !== 1) {
      commit(mutationSetBillingCurrency, originalCurrency);
      commit(mutationError, 'Could not save billing currency');
    }
  },
  async [actionUpgrade]({ commit, state }) {
    commit(mutationError, '');
    commit(mutationSaving, true);
    const {
      billingCurrency: currency,
      planType,
      activeUserCount,
      userCount,
      plan: {
        paymentFrequency: { key: frequency },
        paymentType,
      },
      subscriptionId,
    } = state;
    try {
      const data = await upgrade({
        currency,
        planType,
        activeUserCount,
        userCount,
        frequency,
        subscriptionId,
        confirmed: false,
        campaign: '',
        paymentType,
      });
      const { error = '' } = data;
      if (error) {
        commit(mutationError, 'Could not upgrade plan');
      }
      commit(mutationSaving, false);
      return data;
    } catch (e) {
      commit(mutationError, 'Could not upgrade plan');
      commit(mutationSaving, false);
    }
  },
  async [actionSetUpdateDetails]({ commit, state }, payload: boolean) {
    let details: IUpdateDetails = {
      ...state.updateDetails,
      isLoading: payload,
    };
    commit(mutationError, '');
    commit(mutationSetUpdateDetails, details);
    try {
      const { total, taxes } = await getUpgradeEstimate(
        state.planType,
        state.userCount,
        state.billingCurrency,
        state.subscriptionId
      );
      details = {
        isLoading: false,
        taxes,
        total,
      };
      commit(mutationSetUpdateDetails, details);
    } catch (e) {
      commit(mutationError, 'Could not connect to billing service');
    }
  },
  async [actionSetChargebeeState]({ commit }, payload: EnumChargeBeeState) {
    commit(mutationSetChargebeeState, payload);
  },
};
